import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Progress } from '../../components/ui/progress';
import { 
  Clock, Users, MapPin, Calendar, Download, Settings, 
  CheckCircle, XCircle, Timer, TrendingUp, AlertCircle, User,
  PoundSterling, Truck, Plus, Receipt
} from 'lucide-react';
import api from '../../utils/api';

const TimeTracking = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('live');
  const [employees, setEmployees] = useState([]);
  const [report, setReport] = useState(null);
  const [config, setConfig] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingReport, setLoadingReport] = useState(false);
  const [showPurchaseDialog, setShowPurchaseDialog] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  
  // Report filters
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [selectedEmployee, setSelectedEmployee] = useState('all');

  useEffect(() => {
    fetchEmployeeStatus();
    fetchConfig();
    fetchUsage();
  }, []);

  const fetchEmployeeStatus = async () => {
    try {
      const response = await api.get('/admin/time-tracking/employees');
      setEmployees(response.data.employees || []);
    } catch (error) {
      console.log('Error fetching employees:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsage = async () => {
    try {
      const response = await api.get('/admin/tracking/usage');
      setUsage(response.data);
    } catch (error) {
      console.log('Error fetching usage:', error);
    }
  };

  const purchaseDriverPack = async () => {
    setPurchasing(true);
    try {
      await api.post('/admin/tracking/purchase-driver-pack');
      toast.success('Driver pack purchased! You can now track an additional driver.');
      fetchUsage();
      fetchConfig();
      setShowPurchaseDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to purchase driver pack');
    } finally {
      setPurchasing(false);
    }
  };

  const fetchConfig = async () => {
    try {
      const response = await api.get('/admin/time-tracking/config');
      setConfig(response.data);
    } catch (error) {
      console.log('Error fetching config:', error);
    }
  };

  const fetchReport = async () => {
    setLoadingReport(true);
    try {
      const params = new URLSearchParams({
        start_date: startDate,
        end_date: endDate
      });
      if (selectedEmployee !== 'all') {
        params.append('employee_id', selectedEmployee);
      }
      
      const response = await api.get(`/admin/time-tracking/report?${params}`);
      setReport(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fetch report');
    } finally {
      setLoadingReport(false);
    }
  };

  const saveConfig = async () => {
    try {
      await api.put('/admin/time-tracking/config', config);
      toast.success('Configuration saved');
    } catch (error) {
      toast.error('Failed to save configuration');
    }
  };

  const clockedInCount = employees.filter(e => e.is_clocked_in).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="time-tracking-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Employee Time Tracking</h1>
          <p className="text-gray-600">Monitor employee clock-in/out and work hours</p>
        </div>
        <Badge variant="outline" className="text-lg px-4 py-2">
          <Users className="mr-2" size={18} />
          {clockedInCount} / {employees.length} Clocked In
        </Badge>
      </div>

      {/* White Label Feature Banner */}
      <Card className="bg-gradient-to-r from-purple-500 to-indigo-600 text-white">
        <CardContent className="py-4">
          <div className="flex items-center gap-4">
            <Timer size={32} />
            <div>
              <h3 className="font-bold">White Label Feature</h3>
              <p className="text-sm opacity-90">
                Time tracking with GPS location capture is available on the White Label plan.
                Track employee hours for payroll and project management.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="live" data-testid="tab-live">
            <Users size={16} className="mr-2" />
            Live Status
          </TabsTrigger>
          <TabsTrigger value="costs" data-testid="tab-costs">
            <PoundSterling size={16} className="mr-2" />
            Costs & Limits
          </TabsTrigger>
          <TabsTrigger value="reports" data-testid="tab-reports">
            <TrendingUp size={16} className="mr-2" />
            Reports
          </TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings">
            <Settings size={16} className="mr-2" />
            Settings
          </TabsTrigger>
        </TabsList>

        {/* Costs & Limits Tab */}
        <TabsContent value="costs" className="space-y-4">
          {usage && (
            <>
              {/* Usage Overview */}
              <div className="grid md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-blue-100 rounded-full">
                        <Truck className="text-blue-600" size={24} />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Tracked Drivers</p>
                        <p className="text-2xl font-bold">
                          {usage.summary?.tracked_drivers || 0} / {usage.summary?.max_drivers || 1}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-green-100 rounded-full">
                        <PoundSterling className="text-green-600" size={24} />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Monthly Cost</p>
                        <p className="text-2xl font-bold">
                          £{usage.summary?.cost_this_month?.toFixed(2) || '0.00'}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-purple-100 rounded-full">
                        <Receipt className="text-purple-600" size={24} />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Cost Limit</p>
                        <p className="text-2xl font-bold">
                          £{usage.summary?.cost_limit?.toFixed(2) || '30.00'}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-orange-100 rounded-full">
                        <MapPin className="text-orange-600" size={24} />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">API Requests</p>
                        <p className="text-2xl font-bold">
                          {usage.summary?.requests_this_month || 0}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Budget Progress */}
              <Card>
                <CardHeader>
                  <CardTitle>Monthly Budget</CardTitle>
                  <CardDescription>
                    {usage.summary?.current_month} tracking costs
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between text-sm">
                      <span>Used: £{usage.summary?.cost_this_month?.toFixed(2) || '0.00'}</span>
                      <span>Limit: £{usage.summary?.cost_limit?.toFixed(2) || '30.00'}</span>
                    </div>
                    <Progress 
                      value={((usage.summary?.cost_this_month || 0) / (usage.summary?.cost_limit || 30)) * 100} 
                      className="h-3"
                    />
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-500">
                        Remaining: £{usage.summary?.remaining_budget?.toFixed(2) || '30.00'}
                      </span>
                      {usage.summary?.limit_exceeded && (
                        <Badge variant="destructive">Limit Exceeded</Badge>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Driver Packs */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle>Driver Tracking Packs</CardTitle>
                    <CardDescription>
                      Each pack allows tracking 1 additional driver at £30/month
                    </CardDescription>
                  </div>
                  <Button onClick={() => setShowPurchaseDialog(true)}>
                    <Plus size={16} className="mr-2" />
                    Add Driver Pack
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="p-4 border rounded-lg bg-green-50 border-green-200">
                      <h4 className="font-semibold text-green-900">Included</h4>
                      <p className="text-3xl font-bold text-green-700 my-2">1 Driver</p>
                      <p className="text-sm text-green-600">Free with subscription</p>
                    </div>
                    
                    {Array.from({ length: usage.config?.additional_driver_packs || 0 }).map((_, idx) => (
                      <div key={idx} className="p-4 border rounded-lg bg-blue-50 border-blue-200">
                        <h4 className="font-semibold text-blue-900">Pack {idx + 1}</h4>
                        <p className="text-3xl font-bold text-blue-700 my-2">+1 Driver</p>
                        <p className="text-sm text-blue-600">£30/month</p>
                      </div>
                    ))}
                    
                    <div 
                      className="p-4 border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer hover:bg-gray-50"
                      onClick={() => setShowPurchaseDialog(true)}
                    >
                      <Plus className="text-gray-400 mb-2" size={32} />
                      <p className="text-sm text-gray-500">Add Driver Pack</p>
                      <p className="text-xs text-gray-400">£30/month</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Cost Breakdown */}
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="pt-6">
                  <h4 className="font-semibold text-blue-900 mb-3">Cost Breakdown</h4>
                  <div className="grid md:grid-cols-2 gap-6 text-sm">
                    <div>
                      <p className="font-medium mb-2">Per Driver Estimate (6-min intervals):</p>
                      <ul className="space-y-1 text-blue-800">
                        <li>• {usage.pricing?.estimated_requests_per_driver_month || 1760} requests/month</li>
                        <li>• £{usage.pricing?.cost_per_request_pounds?.toFixed(4) || '0.004'} per request</li>
                        <li>• ~£{usage.pricing?.estimated_cost_per_driver_month || '7'}/month actual cost</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-medium mb-2">Your Flat Rate:</p>
                      <ul className="space-y-1 text-blue-800">
                        <li>• £30/month per driver</li>
                        <li>• Includes 15-min stationary optimization</li>
                        <li>• Auto-invoicing for overages</li>
                        <li>• 85% cheaper than real-time tracking</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Live Status Tab */}
        <TabsContent value="live" className="space-y-4">
          {/* Stats Cards */}
          <div className="grid md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-green-100 rounded-full">
                    <CheckCircle className="text-green-600" size={24} />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Clocked In</p>
                    <p className="text-2xl font-bold text-green-600">{clockedInCount}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-gray-100 rounded-full">
                    <XCircle className="text-gray-600" size={24} />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Clocked Out</p>
                    <p className="text-2xl font-bold text-gray-600">
                      {employees.length - clockedInCount}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-blue-100 rounded-full">
                    <Users className="text-blue-600" size={24} />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Total Staff</p>
                    <p className="text-2xl font-bold text-blue-600">{employees.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-purple-100 rounded-full">
                    <Timer className="text-purple-600" size={24} />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Tracking Interval</p>
                    <p className="text-2xl font-bold text-purple-600">
                      {config?.tracking_interval_seconds || 60}s
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Employee List */}
          <Card>
            <CardHeader>
              <CardTitle>Employee Status</CardTitle>
              <CardDescription>Real-time clock-in status of all employees</CardDescription>
            </CardHeader>
            <CardContent>
              {employees.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Users className="mx-auto mb-4 opacity-50" size={48} />
                  <p>No employees found</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {employees.map((emp) => (
                    <div 
                      key={emp.employee?.id} 
                      className={`flex items-center justify-between p-4 border rounded-lg ${
                        emp.is_clocked_in ? 'bg-green-50 border-green-200' : 'bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`p-2 rounded-full ${emp.is_clocked_in ? 'bg-green-200' : 'bg-gray-200'}`}>
                          <User className={emp.is_clocked_in ? 'text-green-700' : 'text-gray-500'} size={20} />
                        </div>
                        <div>
                          <p className="font-medium">
                            {emp.employee?.first_name} {emp.employee?.last_name}
                          </p>
                          <p className="text-sm text-gray-500 capitalize">
                            {emp.employee?.role?.replace(/_/g, ' ')}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        {emp.is_clocked_in && emp.clock_in_location && (
                          <div className="text-right text-sm">
                            <p className="text-gray-500 flex items-center gap-1">
                              <MapPin size={14} />
                              {emp.clock_in_location.lat?.toFixed(4)}, {emp.clock_in_location.lng?.toFixed(4)}
                            </p>
                            <p className="text-gray-400">
                              Since {new Date(emp.last_clock_in).toLocaleTimeString()}
                            </p>
                          </div>
                        )}
                        
                        <Badge variant={emp.is_clocked_in ? 'default' : 'secondary'} className={emp.is_clocked_in ? 'bg-green-600' : ''}>
                          {emp.is_clocked_in ? 'Clocked In' : 'Clocked Out'}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Reports Tab */}
        <TabsContent value="reports" className="space-y-4">
          {/* Filter Card */}
          <Card>
            <CardHeader>
              <CardTitle>Generate Report</CardTitle>
              <CardDescription>View time tracking data for a date range</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-4 items-end">
                <div>
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
                <div>
                  <Label>End Date</Label>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
                <div>
                  <Label>Employee</Label>
                  <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                    <SelectTrigger className="w-48">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Employees</SelectItem>
                      {employees.map((emp) => (
                        <SelectItem key={emp.employee?.id} value={emp.employee?.id}>
                          {emp.employee?.first_name} {emp.employee?.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={fetchReport} disabled={loadingReport}>
                  {loadingReport ? 'Loading...' : 'Generate Report'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Report Results */}
          {report && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Time Tracking Report</CardTitle>
                  <CardDescription>
                    {report.date_range?.start} to {report.date_range?.end}
                  </CardDescription>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-primary">{report.grand_total_hours} hrs</p>
                  <p className="text-sm text-gray-500">{report.total_entries} entries</p>
                </div>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[500px]">
                  {report.report?.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Calendar className="mx-auto mb-4 opacity-50" size={48} />
                      <p>No time entries found for this period</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {report.report.map((emp) => (
                        <div key={emp.employee_id} className="border rounded-lg overflow-hidden">
                          <div className="bg-gray-50 p-4 flex justify-between items-center">
                            <div>
                              <p className="font-semibold">{emp.employee_name || 'Unknown'}</p>
                              <p className="text-sm text-gray-500 capitalize">{emp.role?.replace(/_/g, ' ')}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-xl font-bold text-primary">{emp.total_hours} hrs</p>
                              <p className="text-sm text-gray-500">{emp.total_entries} entries</p>
                            </div>
                          </div>
                          
                          <div className="p-4">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="border-b">
                                  <th className="text-left py-2">Date</th>
                                  <th className="text-left py-2">Clock In</th>
                                  <th className="text-left py-2">Clock Out</th>
                                  <th className="text-right py-2">Hours</th>
                                  <th className="text-left py-2">Location</th>
                                </tr>
                              </thead>
                              <tbody>
                                {emp.entries?.slice(0, 10).map((entry) => (
                                  <tr key={entry.id} className="border-b last:border-0">
                                    <td className="py-2">
                                      {new Date(entry.clock_in_time).toLocaleDateString()}
                                    </td>
                                    <td className="py-2">
                                      {new Date(entry.clock_in_time).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                    </td>
                                    <td className="py-2">
                                      {entry.clock_out_time 
                                        ? new Date(entry.clock_out_time).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})
                                        : <Badge variant="outline">Active</Badge>
                                      }
                                    </td>
                                    <td className="py-2 text-right font-medium">
                                      {entry.total_hours?.toFixed(2) || '-'}
                                    </td>
                                    <td className="py-2 text-xs text-gray-500">
                                      {entry.clock_in_address?.slice(0, 20)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Tracking Configuration</CardTitle>
              <CardDescription>Configure driver tracking and time tracking settings</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {config && (
                <>
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <p className="font-medium">Enable GPS Tracking</p>
                      <p className="text-sm text-gray-500">Track driver locations during shifts</p>
                    </div>
                    <Switch
                      checked={config.tracking_enabled}
                      onCheckedChange={(val) => setConfig({...config, tracking_enabled: val})}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <p className="font-medium">Track Drivers Only</p>
                      <p className="text-sm text-gray-500">Only track drivers, not porters or other staff (reduces costs)</p>
                    </div>
                    <Switch
                      checked={config.track_drivers_only}
                      onCheckedChange={(val) => setConfig({...config, track_drivers_only: val})}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <p className="font-medium">Require GPS for Clock-In</p>
                      <p className="text-sm text-gray-500">Employees must enable GPS to clock in</p>
                    </div>
                    <Switch
                      checked={config.require_gps_for_clock_in}
                      onCheckedChange={(val) => setConfig({...config, require_gps_for_clock_in: val})}
                    />
                  </div>
                  
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <Label>Tracking Interval (seconds)</Label>
                      <Input
                        type="number"
                        value={config.tracking_interval_seconds}
                        onChange={(e) => setConfig({...config, tracking_interval_seconds: parseInt(e.target.value) || 60})}
                        min={30}
                        max={300}
                      />
                      <p className="text-xs text-gray-500 mt-1">Recommended: 60s (low cost)</p>
                    </div>
                    
                    <div>
                      <Label>Geofence Radius (meters)</Label>
                      <Input
                        type="number"
                        value={config.geofence_radius_meters}
                        onChange={(e) => setConfig({...config, geofence_radius_meters: parseInt(e.target.value) || 100})}
                        min={50}
                        max={500}
                      />
                      <p className="text-xs text-gray-500 mt-1">For arrival detection</p>
                    </div>
                    
                    <div>
                      <Label>Auto Clock-Out (hours)</Label>
                      <Input
                        type="number"
                        value={config.auto_clock_out_hours}
                        onChange={(e) => setConfig({...config, auto_clock_out_hours: parseInt(e.target.value) || 12})}
                        min={8}
                        max={24}
                      />
                      <p className="text-xs text-gray-500 mt-1">Auto clock-out if forgotten</p>
                    </div>
                  </div>
                  
                  <div className="flex justify-end">
                    <Button onClick={saveConfig}>
                      Save Configuration
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
          
          {/* Cost Info */}
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <AlertCircle className="text-blue-600 mt-1" size={24} />
                <div>
                  <h4 className="font-semibold text-blue-900">Cost Optimization</h4>
                  <p className="text-sm text-blue-800 mt-1">
                    Location tracking at 6-minute intervals (15 min when stationary) significantly reduces API costs 
                    compared to real-time tracking. This setting provides adequate visibility for fleet management 
                    while keeping operational costs at just ~£7 per driver per month in API fees.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Purchase Driver Pack Dialog */}
      <Dialog open={showPurchaseDialog} onOpenChange={setShowPurchaseDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Purchase Additional Driver Pack</DialogTitle>
            <DialogDescription>
              Add capacity to track one more driver in your fleet
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="p-4 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg mb-4">
              <h3 className="text-xl font-bold">Driver Tracking Pack</h3>
              <p className="text-3xl font-bold mt-2">£30<span className="text-lg font-normal">/month</span></p>
              <p className="text-sm opacity-90 mt-2">Track 1 additional driver with GPS location updates</p>
            </div>
            
            <div className="space-y-2 text-sm">
              <p className="flex items-center gap-2">
                <CheckCircle className="text-green-500" size={16} />
                6-minute location updates (cost optimized)
              </p>
              <p className="flex items-center gap-2">
                <CheckCircle className="text-green-500" size={16} />
                15-minute updates when stationary
              </p>
              <p className="flex items-center gap-2">
                <CheckCircle className="text-green-500" size={16} />
                Geofencing for arrival detection
              </p>
              <p className="flex items-center gap-2">
                <CheckCircle className="text-green-500" size={16} />
                Real-time dashboard visibility
              </p>
            </div>
            
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                <strong>Current status:</strong> Tracking {usage?.summary?.tracked_drivers || 0} of {usage?.summary?.max_drivers || 1} drivers.
                This pack will increase your limit to {(usage?.summary?.max_drivers || 1) + 1} drivers.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPurchaseDialog(false)}>
              Cancel
            </Button>
            <Button onClick={purchaseDriverPack} disabled={purchasing}>
              {purchasing ? 'Processing...' : 'Purchase for £30/month'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TimeTracking;
